import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod/v3";

import { itemsGetOne, menusGetById } from "~/api-refs";
import { AddExistingItemForm } from "~/components/menus/items/create/add-existing-item-form";
import { CreateNewItemForm } from "~/components/menus/items/create/create-new-item-form";
import { DuplicateItemForm } from "~/components/menus/items/create/duplicate-item-form";
import { ModalTypeForm } from "~/components/menus/items/create/modal-type-form";
import type {
  ItemFormData,
  ModalTypes,
} from "~/components/menus/items/create/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { toConvexTableId } from "~/lib/convex-table-id";

const itemSearchParams = z.object({
  duplicate: z.string().optional(),
  newSalesCategoryId: z.string().optional(),
  newTaxCategoryId: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/menus/$menuId/items/new")(
  {
    component: CreateItemModal,
    validateSearch: itemSearchParams,
  }
);

function CreateItemModal() {
  const { menuId } = Route.useParams();
  const { duplicate, newSalesCategoryId, newTaxCategoryId } = Route.useSearch();
  const navigate = useNavigate();
  const [modalType, setModalType] = useState<ModalTypes | null>(
    duplicate ? "duplicate" : null
  );
  const [item, setItem] = useState<ItemFormData | undefined>(undefined);

  const { data: menu } = useQuery(
    convexQuery(menusGetById, {
      id: toConvexTableId("menus", menuId),
    })
  );

  const { data: duplicateItemData } = useQuery(
    convexQuery(
      itemsGetOne,
      duplicate
        ? {
            itemId: toConvexTableId("items", duplicate),
          }
        : "skip"
    )
  );

  // Auto-populate item when duplicate data is loaded
  useEffect(() => {
    if (duplicateItemData && !item) {
      setItem({
        name: duplicateItemData.name,
        shortName: duplicateItemData.shortName,
        imageUrl: duplicateItemData.imageUrl ?? null,
        description: duplicateItemData.description,
        price:
          typeof duplicateItemData.price === "object"
            ? Math.max(
                ...Object.values(duplicateItemData.price).map((d) =>
                  Number.parseFloat(String(d))
                ),
                0
              ).toString()
            : String(duplicateItemData.price),
        isOutOfStock: duplicateItemData.isOutOfStock,
        categories: duplicateItemData.categoryRelations.map(
          (cr) => cr.category._id
        ),
        taxCategoryId: duplicateItemData.taxCategoryId ?? null,
        salesCategoryId: duplicateItemData.salesCategoryId ?? null,
      });
    }
  }, [duplicateItemData, item]);

  const handleClose = () => {
    void navigate({ to: `/menus/${menuId}/items` });
  };

  const handleFinished = () => {
    handleClose();
  };

  if (modalType === "newItem" || item) {
    return (
      <Dialog onOpenChange={handleClose} open>
        <CreateNewItemForm
          handleClose={handleClose}
          item={item}
          menuId={menuId}
          menuName={menu?.name}
          onFinished={handleFinished}
          searchParams={{ newSalesCategoryId, newTaxCategoryId }}
          setModalType={setModalType}
        />
      </Dialog>
    );
  }

  return (
    <Dialog onOpenChange={handleClose} open>
      <DialogContent>
        <DialogHeader className="items-center space-y-0">
          <DialogTitle>
            {
              {
                null: "Add Item",
                duplicate: "Duplicate Item",
                existingItem: "Add existing",
              }[modalType ?? "null"]
            }
          </DialogTitle>
          <DialogDescription className="body">
            {modalType === null && "How do you want to add your item?"}
            {modalType === "duplicate" && (
              <>Choose an existing item to use as a template</>
            )}
            {modalType === "existingItem" && (
              <>Share and sync items across multiple menus</>
            )}
          </DialogDescription>
        </DialogHeader>
        {modalType === "duplicate" && !item && (
          <DuplicateItemForm setItem={setItem} setModalType={setModalType} />
        )}
        {modalType === "existingItem" && (
          <AddExistingItemForm
            onFinished={handleFinished}
            setModalType={setModalType}
          />
        )}
        {!modalType && (
          <ModalTypeForm onSubmit={(itemType) => setModalType(itemType)} />
        )}
      </DialogContent>
    </Dialog>
  );
}
